import React, { useMemo, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { Badge } from './ui/badge';

/**
 * Multi-selector de provincias con buscador y chips.
 * Props:
 *  - provincias: string[] (catálogo)
 *  - selected:   string[]
 *  - onChange:   (nuevo:string[]) => void
 */
export const ProvinciaSelector = ({ provincias, selected, onChange, autoFocus = true }) => {
  const [q, setQ] = useState('');
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (autoFocus && inputRef.current) {
      // Pequeño delay para que el dialog termine de abrirse
      const t = setTimeout(() => inputRef.current?.focus(), 80);
      return () => clearTimeout(t);
    }
  }, [autoFocus]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return provincias;
    return provincias.filter((p) => p.toLowerCase().includes(qq));
  }, [q, provincias]);

  const toggle = (p) => {
    if (selected.includes(p)) {
      onChange(selected.filter((x) => x !== p));
    } else {
      onChange([...selected, p]);
    }
  };

  return (
    <div className="border border-ink-200 rounded-lg bg-white overflow-hidden" data-testid="provincia-selector">
      <div className="px-3 py-2 border-b border-ink-200 bg-ink-50 flex items-center gap-2">
        <Search className="w-4 h-4 text-ink-400" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Buscar provincia…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-ink-400 text-ink-900"
          data-testid="provincia-search"
        />
        <span className="text-xs text-ink-500 font-medium">
          {selected.length} seleccionada{selected.length !== 1 ? 's' : ''}
        </span>
      </div>

      {selected.length > 0 && (
        <div className="px-3 py-2 border-b border-ink-200 bg-brand-50/50 flex flex-wrap gap-1.5">
          {selected.map((p) => (
            <Badge
              key={p}
              className="bg-white border border-brand-200 text-brand-700 hover:bg-brand-100 cursor-pointer"
              onClick={() => toggle(p)}
              data-testid={`chip-${p}`}
            >
              {p} ✕
            </Badge>
          ))}
        </div>
      )}

      <div className="max-h-56 overflow-y-auto grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-0.5 p-2">
        {filtered.map((p) => {
          const checked = selected.includes(p);
          return (
            <button
              type="button"
              key={p}
              onClick={() => toggle(p)}
              className={`text-left flex items-center gap-2 px-2 py-1.5 rounded text-sm transition-colors ${
                checked
                  ? 'bg-brand-50 text-brand-900 font-medium'
                  : 'text-ink-700 hover:bg-ink-100'
              }`}
              data-testid={`provincia-opt-${p}`}
            >
              <span
                className={`inline-flex w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                  checked ? 'bg-brand-500 border-brand-500 text-white' : 'border-ink-300'
                }`}
              >
                {checked && <Check className="w-3 h-3" strokeWidth={3} />}
              </span>
              {p}
            </button>
          );
        })}
        {filtered.length === 0 && (
          <p className="col-span-full text-center text-sm text-ink-400 py-4">Sin resultados</p>
        )}
      </div>
    </div>
  );
};

export default ProvinciaSelector;
